import React, { useState, useEffect } from "react";
import { Box, Stack, Grid, Slider, Typography } from "@mui/material";
import VSNTypography from "../../../shared/VSNTypography";
import useOTBStyle from "../OTBStyle";
import ViolinBoxPlotChartComponent from "./ViolinBoxPlotChartComponent";
import { HearingLoss, HeartBeat, VisionImpairment } from "../../../shared/icon";

const Vitals = ({ data, pdf }) => {
  const classes = useOTBStyle();

  const visceralFatValues = data?.map((patient) => patient.visceral_fat);

  const numericValues = visceralFatValues?.map(Number);
  const visceralMAX = Math?.max(...numericValues) ?? 0;
  const visceralMIN = Math?.min(...numericValues) ?? 0;
  const [visceralFat, setVisceralFat] = useState([visceralMIN, visceralMAX]);

  useEffect(() => {
    setVisceralFat([visceralMIN, visceralMAX]);
  }, [visceralMIN, visceralMAX]);

  const handleChange1 = (event, newValue) => {
    setVisceralFat(newValue);
  };

  const filteredData = data.filter(
    (patient) =>
      patient.visceral_fat >= visceralFat[0] &&
      patient.visceral_fat <= visceralFat[1]
  );

  const ACSMData = filteredData?.map((item) => item?.acsm_body_fat_percentile);
  const ACSMRanges = [
    { label: "Very Poor", min: 0, max: 15, color: "#C83C32" },
    { label: "Poor", min: 16, max: 40, color: "#F5B1AC" },
    { label: "Fair", min: 41, max: 60, color: "#E8D0B1" },
    { label: "Good", min: 61, max: 80, color: "#F3F6EF" },
    { label: "Excellent", min: 81, max: 90, color: "#C9D9B5" },
    { label: "Very Lean", min: 91, max: 100, color: "#7CAB53" },
  ];

  const BodyFatData = filteredData?.map((item) => item?.body_fat_percentage);
  const BodyFatRanges = [{ label: "", min: 0, max: 60, color: "#FFFFFF" }];

  return (
    <>
      {pdf && (
        <Box
          className={classes.pdfReportLayout}
          sx={{ pageBreakAfter: "always" }}
        >
          <VSNTypography variant="h4">Vitals</VSNTypography>

          <Stack direction="row" gap={2}>
            <Box className={classes.pdfReportCard}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  <VisionImpairment />
                  <VSNTypography variant="h2">Vision Impairment</VSNTypography>
                </Stack>
                <VSNTypography component="span" sx={{ color: "#898994" }}>
                  Dept: {data?.length}{" "}
                  <span
                    style={{
                      color:
                        data?.filter((patient) => patient?.vitals === 1)
                          .length -
                          data?.length <
                        0
                          ? "red"
                          : "green",
                    }}
                  >
                    {" "}
                    (
                    {data?.filter((patient) => patient?.vitals === 1).length -
                      data?.length}
                    )
                  </span>
                </VSNTypography>
              </Stack>
              <Stack direction="row">
                <Box
                  sx={{
                    padding: "15px 32px 15px 0px",
                    borderRight: "1px solid #CBCBCF",
                    width: "100%",
                  }}
                >
                  <VSNTypography variant="h3">Far Acuity {">"}40</VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {data?.filter((patient) => patient.far_acuity === 1).length}
                  </VSNTypography>
                </Box>
                <Box
                  sx={{
                    padding: "15px 32px",
                    borderRight: "1px solid #CBCBCF",
                    width: "100%",
                  }}
                >
                  <VSNTypography variant="h3">
                    Near Acuity {">"}40
                  </VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter((patient) => patient.near_acuity === 1)
                        .length
                    }
                  </VSNTypography>
                </Box>
                <Box sx={{ padding: "15px 0px 15px 32px", width: "100%" }}>
                  <VSNTypography variant="h3">Color Deficit</VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter((patient) => patient?.color_deficit === 1)
                        .length
                    }
                  </VSNTypography>
                </Box>
              </Stack>
            </Box>

            <Box className={classes.pdfReportCard}>
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
              >
                <Stack
                  variant="h2"
                  direction="row"
                  spacing={1}
                  alignItems="center"
                >
                  <HearingLoss />
                  <VSNTypography>Hearing Loss</VSNTypography>
                </Stack>
                <VSNTypography component="span" sx={{ color: "#898994" }}>
                  Dept: {data?.length}
                  <span
                    style={{
                      color:
                        data?.filter((patient) => patient?.vitals === 1)
                          .length -
                          data?.length <
                        0
                          ? "red"
                          : "green",
                    }}
                  >
                    {" "}
                    (
                    {data?.filter((patient) => patient?.vitals === 1).length -
                      data?.length}
                    )
                  </span>
                </VSNTypography>
              </Stack>
              <Stack direction="row">
                <Box
                  sx={{
                    padding: "15px 32px 15px 0px",
                    borderRight: "1px solid #CBCBCF",
                    width: "100%",
                  }}
                >
                  <VSNTypography variant="h3">{">"}40 Db (Left)</VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter(
                        (patient) =>
                          patient.hearing_greater_than_40_db_left === 1
                      ).length
                    }
                  </VSNTypography>
                </Box>
                <Box
                  sx={{
                    padding: "15px 32px",
                    width: "100%",
                  }}
                >
                  <VSNTypography variant="h3">{">"}40 Db (right)</VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter(
                        (patient) =>
                          patient.hearing_greater_than_40_db_right === 1
                      ).length
                    }
                  </VSNTypography>
                </Box>
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" gap={2} mt={2}>
            <Stack direction="column" gap={2}>
              <Box className={classes.pdfReportCard}>
                <Stack direction="row" mb={0.5} spacing={1} alignItems="center">
                  <HeartBeat />
                  <VSNTypography variant="h2">
                    Hypertension Stage 1
                  </VSNTypography>
                </Stack>
                <Box>
                  <VSNTypography variant="h3">
                    Systolic {">"} 129 or Diastolic {">"} 80
                  </VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter(
                        ({ hypertension_stage_1 }) => hypertension_stage_1 === 1
                      ).length
                    }
                  </VSNTypography>
                </Box>
              </Box>

              <Box className={classes.pdfReportCard}>
                <Stack direction="row" mb={0.5} spacing={1} alignItems="center">
                  <HeartBeat />
                  <VSNTypography variant="h2">
                    Hypertension Stage 2
                  </VSNTypography>
                </Stack>
                <Box>
                  <VSNTypography variant="h3">
                    Systolic {">"} 139 or Diastolic {">"} 89
                  </VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter(
                        ({ hypertension_stage_2 }) => hypertension_stage_2 === 1
                      ).length
                    }
                  </VSNTypography>
                </Box>
              </Box>
              <Box className={classes.pdfReportCard}>
                <Stack direction="row" mb={0.5} spacing={1} alignItems="center">
                  <HeartBeat />
                  <VSNTypography variant="h2">
                    Critical Hypertension
                  </VSNTypography>
                </Stack>
                <Box>
                  <VSNTypography variant="h3">
                    Systolic {">"} 179 or Diastolic {">"} 119
                  </VSNTypography>
                  <VSNTypography
                    className="record"
                    sx={{ marginTop: "0 !important" }}
                  >
                    {
                      data?.filter(
                        ({ critical_hypertension }) =>
                          critical_hypertension === 1
                      ).length
                    }
                  </VSNTypography>
                </Box>
              </Box>
            </Stack>

            <Stack>
              <ViolinBoxPlotChartComponent
                data={data}
                title="ACSM Body Fat Percentile"
                yData={ACSMData}
                ranges={ACSMRanges}
                height="450px"
                width="400px"
                bodyPercentage={[-1, 101]}
              />
            </Stack>

            <Stack>
              <ViolinBoxPlotChartComponent
                data={data}
                title="Body Fat %"
                yData={BodyFatData}
                ranges={BodyFatRanges}
                height="440px"
                width="300px"
                bodyPercentage={[0, 60]}
              />
            </Stack>
          </Stack>

          <VSNTypography
            sx={{
              marginTop: 1,
              fontSize: "14px",
              color: "#3A3A40",
              lineHeight: "20px",
              fontWeight: 400,
            }}
          >
            Blood pressure measurements based on individual average of all
            recorded measurements during assessments (Vitals and/or CPET).
          </VSNTypography>
        </Box>
      )}

      {!pdf && (
        <Box>
          <Box className={classes.reportLayout}>
            <div className={classes.stickyTitle}>
              <VSNTypography variant="h2">Vitals</VSNTypography>
            </div>
            <Box>
              <Grid container columnSpacing={1}>
                <Grid item md={6.5} xs={12}>
                  <Box className={classes.reportCard}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ marginBottom: 2 }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <VisionImpairment />
                        <VSNTypography>Vision Impairment</VSNTypography>
                      </Stack>
                      <VSNTypography component="span" sx={{ color: "#898994" }}>
                        Dept: {data?.length}
                        <span
                          style={{
                            color:
                              data?.filter(({ vitals }) => vitals === 1)
                                .length -
                                data?.length <
                              0
                                ? "red"
                                : "green",
                          }}
                        >
                          {" "}
                          (
                          {data?.filter(({ vitals }) => vitals === 1).length -
                            data?.length}
                          )
                        </span>
                      </VSNTypography>
                    </Stack>
                    <Stack direction="row">
                      <Box
                        py={2}
                        pr={{ xxl: 4, lg: 3, xs: 2 }}
                        sx={{
                          borderRight: "1px solid #CBCBCF",
                          width: "100%",
                        }}
                      >
                        <VSNTypography variant="h3">
                          Far Acuity {">"} 40
                        </VSNTypography>
                        <VSNTypography
                          className="record"
                          sx={{ marginTop: "0 !important" }}
                        >
                          {
                            data?.filter(({ far_acuity }) => far_acuity === 1)
                              .length
                          }
                        </VSNTypography>
                      </Box>
                      <Box
                        py={2}
                        px={{ xxl: 4, lg: 3, xs: 2 }}
                        sx={{
                          borderRight: "1px solid #CBCBCF",
                          width: "100%",
                        }}
                      >
                        <VSNTypography variant="h3">
                          Near Acuity {">"} 40
                        </VSNTypography>
                        <VSNTypography
                          className="record"
                          sx={{ marginTop: "0 !important" }}
                        >
                          {
                            data?.filter(({ near_acuity }) => near_acuity === 1)
                              .length
                          }
                        </VSNTypography>
                      </Box>
                      <Box
                        py={2}
                        pl={{ xxl: 4, lg: 3, xs: 2 }}
                        sx={{ width: "100%" }}
                      >
                        <VSNTypography variant="h3">
                          Color Deficit
                        </VSNTypography>
                        <VSNTypography
                          className="record"
                          sx={{ marginTop: "0 !important" }}
                        >
                          {
                            data?.filter(
                              ({ color_deficit }) => color_deficit === 1
                            ).length
                          }
                        </VSNTypography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item md={5.5} sm={6} xs={12}>
                  <Box className={classes.reportCard}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ marginBottom: 2 }}
                    >
                      <Stack direction="row" spacing={2} alignItems="center">
                        <HearingLoss />
                        <VSNTypography>Hearing Loss</VSNTypography>
                      </Stack>
                      <VSNTypography component="span" sx={{ color: "#898994" }}>
                        Dept: {data?.length}
                        <span
                          style={{
                            color:
                              data?.filter(({ vitals }) => vitals === 1)
                                .length -
                                data?.length <
                              0
                                ? "red"
                                : "green",
                          }}
                        >
                          {" "}
                          (
                          {data?.filter(({ vitals }) => vitals === 1).length -
                            data?.length}
                          )
                        </span>
                      </VSNTypography>
                    </Stack>
                    <Stack direction="row">
                      <Box
                        py={2}
                        pr={{ xxl: 4, lg: 3, xs: 2 }}
                        sx={{
                          borderRight: "1px solid #CBCBCF",
                          width: "100%",
                        }}
                      >
                        <VSNTypography variant="h3">
                          {">"}40 Db (Left)
                        </VSNTypography>
                        <VSNTypography
                          className="record"
                          sx={{ marginTop: "0 !important" }}
                        >
                          {
                            data?.filter(
                              ({ hearing_greater_than_40_db_left }) =>
                                hearing_greater_than_40_db_left === 1
                            )?.length
                          }
                        </VSNTypography>
                      </Box>
                      <Box
                        py={2}
                        px={{ xxl: 4, lg: 3, xs: 2 }}
                        sx={{
                          width: "100%",
                        }}
                      >
                        <VSNTypography variant="h3">
                          {">"}40 Db (Right)
                        </VSNTypography>
                        <VSNTypography
                          className="record"
                          sx={{ marginTop: "0 !important" }}
                        >
                          {
                            data?.filter(
                              (hearing_greater_than_40_db_right) =>
                                hearing_greater_than_40_db_right === 1
                            ).length
                          }
                        </VSNTypography>
                      </Box>
                    </Stack>
                  </Box>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                  <Box className={classes.reportCard}>
                    <Stack
                      direction="row"
                      mb={2}
                      spacing={2}
                      alignItems="center"
                    >
                      <HeartBeat />
                      <VSNTypography>Hypertension Stage 1</VSNTypography>
                    </Stack>
                    <Box>
                      <VSNTypography variant="h3">
                        Systolic {">"} 129 or Diastolic {">"} 80
                      </VSNTypography>
                      <VSNTypography
                        className="record"
                        sx={{ marginTop: "0 !important" }}
                      >
                        {
                          data?.filter(
                            ({ hypertension_stage_1 }) =>
                              hypertension_stage_1 === 1
                          )?.length
                        }
                      </VSNTypography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                  <Box className={classes.reportCard}>
                    <Stack
                      direction="row"
                      mb={2}
                      spacing={2}
                      alignItems="center"
                    >
                      <HeartBeat />
                      <VSNTypography>Hypertension Stage 2</VSNTypography>
                    </Stack>
                    <Box>
                      <VSNTypography variant="h3">
                        Systolic {">"} 139 or Diastolic {">"} 89
                      </VSNTypography>
                      <VSNTypography
                        className="record"
                        sx={{ marginTop: "0 !important" }}
                      >
                        {
                          data?.filter(
                            ({ hypertension_stage_2 }) =>
                              hypertension_stage_2 === 1
                          )?.length
                        }
                      </VSNTypography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item md={4} sm={6} xs={12}>
                  <Box className={classes.reportCard}>
                    <Stack
                      direction="row"
                      mb={2}
                      spacing={2}
                      alignItems="center"
                    >
                      <HeartBeat />
                      <VSNTypography>Critical Hypertension</VSNTypography>
                    </Stack>
                    <Box>
                      <VSNTypography variant="h3">
                        Systolic {">"} 179 or Diastolic {">"} 119
                      </VSNTypography>
                      <VSNTypography
                        className="record"
                        sx={{ marginTop: "0 !important" }}
                      >
                        {
                          data?.filter(
                            ({ critical_hypertension }) =>
                              critical_hypertension === 1
                          )?.length
                        }
                      </VSNTypography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              <VSNTypography
                sx={{
                  my: 2,
                  fontSize: "20px",
                  color: "#3A3A40",
                  lineHeight: "30px",
                  fontWeight: 400,
                }}
              >
                Blood pressure measurements based on individual average of all
                recorded measurements during assessments (Vitals and/or CPET).
              </VSNTypography>
            </Box>
            <Stack direction="row" justifyContent="end">
              <Stack
                spacing={2}
                pt={{ md: 2, xs: 2 }}
                pb={1}
                px={{ md: 4, xs: 3 }}
                sx={{
                  backgroundColor: "#F1E1CD",
                  // maxHeight: "150px",
                  borderRadius: "12px",
                }}
                mb={2}
              >
                <Stack
                  direction="row"
                  spacing={4}
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>
                    <VSNTypography
                      variant="h6"
                      sx={{
                        cursor: "pointer",
                        fontWeight: 700,
                        color: "#0A0A0B",
                        lineHeight: "31.5px",
                      }}
                    >
                      Visceral Fat
                    </VSNTypography>
                    <VSNTypography
                      variant="body2"
                      sx={{
                        cursor: "pointer",
                        fontSize: "18px",
                        fontWeight: 500,
                        color: "#6E7079",
                        lineHeight: "23.44px",
                      }}
                    >
                      Low ({visceralMIN}) - High ({visceralMAX})
                    </VSNTypography>
                  </Box>
                  <VSNTypography
                    component="h4"
                    variant="h4"
                    sx={{ fontSize: "40px" }}
                  >
                    {data?.filter(({ vitals }) => vitals === 1)?.length}
                  </VSNTypography>
                </Stack>
                <Box>
                  <Slider
                    value={visceralFat}
                    onChange={handleChange1}
                    valueLabelDisplay="auto"
                    color="natural"
                    min={visceralMIN}
                    max={visceralMAX}
                  />
                </Box>
              </Stack>
            </Stack>

            <Grid container spacing={4}>
              <Grid item smd={6} xs={12}>
                <Box sx={{ height: { smd: 470, xs: 400 } }}>
                  <ViolinBoxPlotChartComponent
                    data={data}
                    title="ACSM Body Fat Percentile"
                    yData={ACSMData}
                    ranges={ACSMRanges}
                    height="100%"
                    width="100%"
                    bodyPercentage={[-1, 101]}
                  />
                </Box>
              </Grid>

              <Grid item smd={6} xs={12}>
                <Box sx={{ height: { smd: 470, xs: 400 } }}>
                  <ViolinBoxPlotChartComponent
                    data={data}
                    title="Body Fat %"
                    yData={BodyFatData}
                    ranges={BodyFatRanges}
                    height="100%"
                    width="100%"
                    bodyPercentage={[0, 60]}
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      )}
    </>
  );
};

export default Vitals;
